import { Notebook, Send, Timer } from "lucide-react";
import React, { useState, useMemo, useEffect } from "react";
import QuickLinksOverlay from "./QuickLinksOverlay";

// API QuickLink interface
interface ApiQuickLink {
  id?: string;
  name: string;
  value: string;
  type: 'url' | 'application';
  icon: string | null;
  description?: string | null;
  is_visible?: boolean;
  is_favorite?: boolean;
  team_id?: string | number | null;
  user_id?: string | number | null;
  order?: number;
  created_at?: string;
  updated_at?: string;
}

interface QuickLinksResponse {
  all: ApiQuickLink[];
  grouped: {
    team_id: string | number | null;
    team_name: string;
    links: ApiQuickLink[];
  }[];
  favorites: ApiQuickLink[];
}

// Chrome bookmark API interface
interface ChromeBookmark {
  id: string;
  title: string;
  url?: string;
  children?: ChromeBookmark[];
  parentId?: string;
  index?: number;
  dateAdded?: number;
  dateGroupModified?: number;
  folderType?: string;
}

// Browser bookmark with folder support (matching QuickLinksOverlay interface)
interface BrowserBookmark {
  id: string;
  title: string;
  url?: string;
  icon?: string;
  type: 'bookmark' | 'folder';
  children?: BrowserBookmark[];
  parentId?: string;
  index?: number;
  dateAdded?: number;
  dateGroupModified?: number;
}

// Frontend display interface
interface DisplayQuickLink {
  title: string;
  url: string;
  icon: string;
  source: 'api' | 'browser';
}

interface QuickLinksPillProps {
  quickLinks: QuickLinksResponse;
  onShowNotesOverlay: () => void;
  onShowPomodoroSetup: () => void;
}

const QuickLinksPill: React.FC<QuickLinksPillProps> = ({ 
  quickLinks, 
  onShowNotesOverlay, 
  onShowPomodoroSetup
}) => {
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [chromeBookmarks, setChromeBookmarks] = useState<ChromeBookmark[]>([]);

  // Load browser bookmarks
  useEffect(() => {
    loadBookmarks();
  }, []);

  const loadBookmarks = async () => {
    if (typeof chrome !== 'undefined' && chrome.bookmarks) {
      try {
        const bookmarkTree = await chrome.bookmarks.getTree();
        console.log('🔍 Debug - Raw bookmark tree from chrome:', bookmarkTree);
        
        // Store the Chrome API structure
        setChromeBookmarks(bookmarkTree);
        console.log('🔍 Debug - Set chromeBookmarks to:', bookmarkTree);
      } catch (error) {
        console.log('Could not load browser bookmarks:', error);
      }
    }
  };

  // Handle bookmark updates
  const handleBookmarksUpdate = () => {
    loadBookmarks();
  };

  // Handle escape key to close overlay
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOverlayOpen) {
        setIsOverlayOpen(false);
      }
    };

    if (isOverlayOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOverlayOpen]);

  // Get favicon for URL - simplified
  const getFaviconUrl = (url: string): string => {
    try {
      const domain = new URL(url).hostname;
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
    } catch {
      return '';
    }
  };

  // Get domain-based icon name for custom icons
  const getDomainIcon = (url: string): string => {
    try {
      const domain = new URL(url).hostname.toLowerCase();
      
      if (domain.includes('google.com')) return 'google';
      if (domain.includes('gmail.com')) return 'gmail';
      if (domain.includes('youtube.com')) return 'youtube';
      if (domain.includes('facebook.com')) return 'facebook';
      if (domain.includes('twitter.com') || domain.includes('x.com')) return 'twitter';
      if (domain.includes('instagram.com')) return 'instagram';
      if (domain.includes('linkedin.com')) return 'linkedin';
      if (domain.includes('github.com')) return 'github';
      
      return 'link';
    } catch {
      return 'link';
    }
  };

  // Find bookmarks bar folder by folderType property (much more reliable than name matching)
  const findBookmarksBarFolder = (bookmarkNodes: ChromeBookmark[]): ChromeBookmark | null => {
    const searchInNodes = (nodes: ChromeBookmark[]): ChromeBookmark | null => {
      for (const node of nodes) {
        // Check for bookmarks-bar folderType
        if (node.folderType === 'bookmarks-bar') {
          console.log('🔍 Found bookmarks bar folder by folderType:', node.title, 'ID:', node.id);
            return node;
          }
          
          // Recursively search in children
        if (node.children) {
          const found = searchInNodes(node.children);
          if (found) return found;
        }
      }
      return null;
    };

    return searchInNodes(bookmarkNodes);
  };

  // Get only direct bookmarks from bookmarks bar (no subfolders)
  const getDirectBookmarksFromBar = (bookmarksBarFolder: ChromeBookmark | null): ChromeBookmark[] => {
    if (!bookmarksBarFolder || !bookmarksBarFolder.children) {
      console.log('🔍 No bookmarks bar folder found, searching one level deeper for fallback');
      
      // Fallback: search one level deeper in the bookmark tree structure
      const fallbackBookmarks: ChromeBookmark[] = [];
      
      const searchOneLevelDeeper = (nodes: ChromeBookmark[]) => {
        for (const node of nodes) {
          if (node.children) {
            // Look for direct bookmarks in the children (one level deeper)
            for (const child of node.children) {
              if (child.url && child.title) {
                fallbackBookmarks.push(child);
              }
            }
          }
        }
      };
      
      searchOneLevelDeeper(chromeBookmarks);
      console.log('🔍 Found fallback bookmarks one level deeper:', fallbackBookmarks.length);
      return fallbackBookmarks.slice(0, 10);
    }
    
    console.log('🔍 Using direct bookmarks from bar folder:', bookmarksBarFolder.title);
    console.log('🔍 Children in bookmarks bar:', bookmarksBarFolder.children.length);
    
    // Only get direct children that are bookmarks (have URLs), not folders
    const directBookmarks = bookmarksBarFolder.children.filter(child => {
      const isBookmark = child.url && child.title;
      if (isBookmark) {
        console.log('🔍 Found direct bookmark:', child.title, '->', child.url);
      } else if (child.children) {
        console.log('🔍 Skipping folder:', child.title);
      }
      return isBookmark;
    });
    
    console.log('🔍 Total direct bookmarks in bar:', directBookmarks.length);
    return directBookmarks;
  };

  // Flatten bookmark tree and extract URLs (used for overlay hierarchical view)
  const flattenBookmarks = (bookmarkNodes: ChromeBookmark[]): ChromeBookmark[] => {
    const result: ChromeBookmark[] = [];
    
    const traverse = (nodes: ChromeBookmark[]) => {
      for (const node of nodes) {
        if (node.url) {
          result.push(node);
        }
        if (node.children && node.children.length > 0) {
          traverse(node.children);
        }
      }
    };
    
    traverse(bookmarkNodes);
    return result;
  };

  // Get bookmarks bar folder and its bookmarks
  const bookmarksBarFolder = useMemo(() => {
    if (chromeBookmarks.length === 0) return null;
    return findBookmarksBarFolder(chromeBookmarks);
  }, [chromeBookmarks]);

  // Get bookmarks from bookmarks bar only
  const bookmarksBarBookmarks = useMemo(() => {
    const directBookmarks = getDirectBookmarksFromBar(bookmarksBarFolder);
    console.log('🔍 Direct bookmarks from bar:', directBookmarks);
    return directBookmarks;
  }, [bookmarksBarFolder, chromeBookmarks]);

  // Transform Chrome bookmark tree to our BrowserBookmark structure for overlay
  const transformBookmarkTree = (bookmarkNodes: ChromeBookmark[]): BrowserBookmark[] => {
    return bookmarkNodes.map(node => ({
      id: node.id,
      title: node.title,
      url: node.url,
      type: node.url ? 'bookmark' : 'folder',
      icon: node.url ? getDomainIcon(node.url) : undefined,
      children: node.children ? transformBookmarkTree(node.children) : undefined,
      parentId: node.parentId,
      index: node.index,
      dateAdded: node.dateAdded,
      dateGroupModified: node.dateGroupModified,
    }));
  };

  // Get hierarchical bookmarks for overlay (all bookmarks)
  const hierarchicalBookmarks = useMemo(() => {
    if (chromeBookmarks.length === 0) return [];
    
    console.log('🔍 Debug - Transforming all bookmarks for overlay:', chromeBookmarks);
    const transformed = transformBookmarkTree(chromeBookmarks);
    console.log('🔍 Debug - Transformed bookmarks:', transformed);
    return transformed;
  }, [chromeBookmarks]);

  // Transform browser bookmarks to display format (only from bookmarks bar)
  const browserLinks: DisplayQuickLink[] = useMemo(() => {
    console.log('🔍 Debug - Raw bookmarksBarBookmarks:', bookmarksBarBookmarks);
    const filtered = bookmarksBarBookmarks.filter(bookmark => bookmark.url && bookmark.title);
    console.log('🔍 Debug - Filtered bookmarksBarBookmarks:', filtered);
    const limited = filtered.slice(0, 10);
    console.log('🔍 Debug - Limited bookmarksBarBookmarks:', limited);
    
    return limited.map(bookmark => ({
      title: bookmark.title,
      url: bookmark.url!,
      icon: getDomainIcon(bookmark.url!),
      source: 'browser' as const
    }));
  }, [bookmarksBarBookmarks]);

  // Transform API quicklinks to display format
  const apiLinks: DisplayQuickLink[] = useMemo(() => {
    if (quickLinks && quickLinks.all && quickLinks.all.length > 0) {
      console.log("🔍 Debug - API quicklinks:", quickLinks.all);
      return quickLinks.all.map(link => ({
        title: link.name,
        url: link.value,
        icon: link.icon || 'link',
        source: 'api' as const
      }));
    }
    
    console.log("🔍 Debug - No API quicklinks found");
    return [];
  }, [quickLinks]);

  // Combine API links and browser bookmarks
  const allLinks: DisplayQuickLink[] = useMemo(() => {
    const combined = [...apiLinks, ...browserLinks];
    console.log('🔍 Debug - Combined all links:', combined);
    
    // Remove duplicates based on URL
    const uniqueLinks = combined.filter((link, index, array) => 
      array.findIndex(l => l.url === link.url) === index
    );
    
    console.log('🔍 Debug - Unique links after deduplication:', uniqueLinks);
    return uniqueLinks;
  }, [apiLinks, browserLinks]);

  // Favorite links for main display (prioritize API links, then browser bookmarks)
  const favoriteLinks: DisplayQuickLink[] = useMemo(() => {
    // Return all links without limiting to 6
    const result = allLinks;
    console.log('🔍 Debug - Favorite links for main pill:', result);
    return result;
  }, [allLinks]);

  // Most used/favorite links for overlay
  const mostUsedLinks: DisplayQuickLink[] = useMemo(() => {
    // For now, just take the first few from each source
    // In a real implementation, this could be based on usage statistics
    const apiFavorites = apiLinks.slice(0, 8);
    const browserFavorites = browserLinks.slice(0, 8);
    return [...apiFavorites, ...browserFavorites];
  }, [apiLinks, browserLinks]);

  const groupedLinks = useMemo(() => {
    return quickLinks?.grouped || [];
  }, [quickLinks]);

  // Don't render anything if no links are available
  if (!favoriteLinks || favoriteLinks.length === 0) {
    return null;
  }

  // Simple icon rendering function
  const renderIcon = (iconName: string, url?: string, source?: 'api' | 'browser') => {
    // If there's no icon from API and we have a URL, try favicon
    if ((!iconName || iconName === 'link') && url) {
      const faviconUrl = getFaviconUrl(url);
      if (faviconUrl) {
        return (
          <img 
            src={faviconUrl}
            alt=""
            className="w-4 h-4"
            onError={(e) => {
              // Fallback to link icon if favicon fails
              e.currentTarget.outerHTML = `
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              `;
            }}
          />
        );
      }
    }

    // Custom icons for known services
    switch (iconName?.toLowerCase()) {
      case 'google':
        return (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
        );
      case 'gmail':
        return (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z" fill="#EA4335"/>
          </svg>
        );
      case 'youtube':
        return (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="#FF0000"/>
          </svg>
        );
      case 'facebook':
        return (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="#1877F2"/>
          </svg>
        );
      case 'twitter':
      case 'x':
        return (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
        );
      case 'instagram':
        return (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
          </svg>
        );
      case 'linkedin':
        return (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
          </svg>
        );
      case 'github':
        return (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
          </svg>
        );
      default:
        // Default link icon
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
        );
    }
  };

  return (
    <div className="flex justify-left">
      <div className="relative inline-flex items-center">
        {/* Main Pill Container */}
        <div className="flex items-center bg-background dark:bg-background rounded-full px-[0.3rem] py-[0.25rem]">
          
          {/* Favorite Links */}
          <div className="flex items-center">
            {favoriteLinks.map((link, index) => (
              <div key={index} className="flex items-center group relative">
                <a 
                  href={link.url}
                  rel="noopener noreferrer"
                  className="flex items-center justify-center p-3 hover:bg-gray-100 dark:hover:bg-gray-700/60 rounded-full transition-all duration-200 mx-1"
                >
                  <span className="text-primary/80 group-hover:text-primary transition-colors">
                    {renderIcon(link.icon, link.url, link.source)}
                  </span>
                </a>
                <div className="absolute left-1/2 transform -translate-x-1/2 translate-y-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 px-2 py-1 rounded text-xs whitespace-nowrap bg-background dark:bg-background border border-border dark:border-border shadow-sm z-50">
                  {link.title}
                </div>
              </div>
            ))}
          </div>

          {/* Divider */}
          <div className="w-px h-8 bg-gray-200 dark:bg-gray-700 mx-2"></div>

          {/* Dropdown Button */}
          <div className="relative">
            <button
              onClick={() => setIsOverlayOpen(!isOverlayOpen)}
              className="flex items-center justify-center p-3 hover:bg-gray-100 dark:hover:bg-gray-700/60 rounded-full transition-all duration-200 group mr-2"
              title="Alle bookmarks bekijken"
            >
              <svg 
                className={`w-4 h-4 text-gray-600 dark:text-gray-300 group-hover:text-primary transition-all duration-200`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* QuickLinks Overlay Component */}
        <QuickLinksOverlay
          isOpen={isOverlayOpen}
          onClose={() => setIsOverlayOpen(false)}
          quickLinks={quickLinks}
          browserBookmarks={hierarchicalBookmarks}
          mostUsedLinks={mostUsedLinks}
          groupedLinks={groupedLinks}
          renderIcon={renderIcon}
          onBookmarksUpdate={handleBookmarksUpdate}
        />
      </div>
    </div>
  );
};

export default QuickLinksPill; 